/**
 * WxAlertVectorLayer
 *
 * A Leaflet GridLayer that fetches GeoJSON alert tiles from the Visual Crossing
 * API and renders them as interactive vector polygons — complete with per-alert
 * fill/stroke styling and mouse-over tooltips.
 *
 * Design decisions:
 *  • Polygons are returned **unclipped** by the server. Every alert whose
 *    boundary intersects a tile is included with its full geometry. To avoid
 *    rendering the same polygon twice where tiles overlap, features are
 *    deduplicated by their `id` property.
 *  • Each Feature carries pre-computed `fillColor`, `fillOpacity`,
 *    `strokeColor`, `strokeWeight`, and metadata (`event`, `headline`,
 *    `severity`, `onset`, `ends`, `link`) so the client can render a fully
 *    interactive layer without additional API calls.
 *
 * Usage
 * -----
 *   import { createAlertVectorLayer } from '/src/weather-map/WxAlertVectorLayer.js';
 *
 *   const alertLayer = createAlertVectorLayer(L, {
 *     key:       'YOUR_API_KEY',
 *     serverRoot:'https://maps.visualcrossing.com',
 *     opacity:   0.7,
 *     unitGroup: 'us',
 *     pane:      'overlayPane',        // optional
 *     getDateTimeFn: () => new Date(),  // optional — returns current map time
 *   });
 *   alertLayer.addTo(map);
 *
 *   // To update displayed time:
 *   alertLayer.setAlertTime(newDate);
 *
 *   // Cleanup:
 *   alertLayer.removeFrom(map);
 */

/**
 * Create a new Leaflet layer that displays weather-alert polygons as interactive
 * GeoJSON features, loaded per tile.
 *
 * @param {L} L  - The Leaflet namespace (must be the same instance used by the map).
 * @param {Object} opts
 * @param {string} opts.key           - API key.
 * @param {string} opts.serverRoot    - Tile server base URL (no trailing slash).
 * @param {number} [opts.opacity=0.7] - Layer opacity.
 * @param {string} [opts.unitGroup='us']
 * @param {string} [opts.pane]        - Leaflet map pane to render into.
 * @param {Function} [opts.getDateTimeFn] - Returns Date for current map time.
 * @returns {L.LayerGroup} A Leaflet LayerGroup wrapping the GridLayer + vector features.
 */

// Inject tooltip CSS once
let _cssInjected = false;
function injectTooltipCSS() {
  if (_cssInjected || typeof document === 'undefined') return;
  _cssInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    .alert-vector-tooltip {
      max-width: 340px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      line-height: 1.4;
      padding: 8px 10px;
    }
    .alert-vector-tooltip .leaflet-tooltip-content {
      margin: 0;
    }
    .alert-headline-clamp {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .alert-copy-id-btn:hover {
      color: #333;
    }
    .alert-copy-id-btn.copied {
      color: #198754;
    }
    .alert-vector-popup {
      min-width: 240px;
      max-width: 380px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      line-height: 1.45;
    }
    .alert-vector-popup .leaflet-popup-content {
      margin: 10px 12px;
    }



  `;
  document.head.appendChild(style);
}

export function createAlertVectorLayer(L, opts = {}) {
  injectTooltipCSS();
  const {
    key,
    serverRoot = 'https://maps.visualcrossing.com',
    opacity = 0.7,
    unitGroup = 'us',
    pane,
    getDateTimeFn,
  } = opts;

  // ---------- state ----------
  /** Abort controllers keyed by tile coord string */
  const abortControllers = new Map();
  /** Set of alert ids already rendered — used for cross-tile dedup */
  const renderedAlertIds = new Set();
  /** Tile keys already fetched (successful) — avoids re-fetching on pan back */
  const fetchedTiles = new Set();
  /** Current datetime override (ISO string) */
  let currentTime = null;
  /** Current opacity multiplier */
  let currentOpacity = opacity;
  /** Set of event types hidden by the user filter (lowercased) */
  const hiddenEventTypes = new Set();
  /** Map of event type (lowercased) → { label, color, count } for filter UI */
  const knownEventTypes = new Map();

  /** Max concurrent fetches — avoids saturating browser connections */
  const MAX_CONCURRENT = 4;
  /** Cap tile zoom — alerts don't need street-level tiling; higher zoom = more
   *  tiles but finer server-side simplification for better detail when zoomed in */
  const ALERT_MAX_ZOOM = 7;
  /** Minimum tile zoom — even fully zoomed out we don't go below this */
  const ALERT_MIN_ZOOM = 3;
  /** Queue of {tileKey, url} objects waiting to be fetched */
  let fetchQueue = [];
  /** Number of fetches currently in-flight */
  let inFlightCount = 0;
  /** The display zoom used for the last fetch round (for re-fetch on zoom change) */
  let lastDisplayZoom = -1;
  /** Reference to the map */
  let _map = null;
  /** Canvas renderer — far faster than SVG for dense alert polygons */
  const canvasRenderer = L.canvas({ padding: 0.5, pane: pane || 'overlayPane' });

  const wrapperGroup = L.layerGroup();

  // Single shared GeoJSON layer — all alert features are added here via addData().
  const sharedGeoLayer = L.geoJSON(null, {
    renderer: canvasRenderer,
    coordsToLatLng(coords) {
      let lng = coords[0];
      const lat = coords[1];
      lng = ((lng + 180) % 360 + 360) % 360 - 180;
      return L.latLng(lat, lng, coords[2]);
    },
    style(feature) {
      const p = feature.properties || {};
      return {
        fillColor:   p.fillColor   || '#999999',
        fillOpacity: (p.fillOpacity != null ? p.fillOpacity : 0.35) * currentOpacity,
        color:       p.strokeColor || p.fillColor || '#999999',
        weight:      p.strokeWeight || 2,
        opacity:     (p.strokeOpacity != null ? p.strokeOpacity : 1) * currentOpacity,
        smoothFactor: 0, // server already simplifies — disable Leaflet's own DP pass
      };
    },
    pane: pane || 'overlayPane',
    onEachFeature(feature, layer) {
      const p = feature.properties || {};
      // Prefer the original alert ID for display; the feature-level id
      // may be a composite (alertId_p{index}) used for cross-tile dedup.
      const alertId = p.alertId || feature.id || p.id || '';
      const html = buildTooltipHtml(p);
      if (html) {
        layer.bindTooltip(html, {
          sticky: true,
          direction: 'top',
          offset: [0, -8],
          className: 'alert-vector-tooltip',
        });
      }
      const popupHtml = buildPopupHtml(p, alertId);
      if (popupHtml) {
        layer.bindPopup(popupHtml, {
          maxWidth: 380,
          className: 'alert-vector-popup',
        });
      }
      let popupOpen = false;
      const baseStyle = {
        color:       p.strokeColor || p.fillColor || '#999999',
        weight:      p.strokeWeight || 2,
        opacity:     (p.strokeOpacity != null ? p.strokeOpacity : 1) * currentOpacity,
        fillColor:   p.fillColor || '#999999',
        fillOpacity: (p.fillOpacity != null ? p.fillOpacity : 0.35) * currentOpacity,
      };
      layer.on('mouseover', function () {
        if (popupOpen) return;
        this.setStyle({
          weight: 4,
          fillOpacity: baseStyle.fillOpacity + 0.15,
        });
        if (this._map) this.bringToFront();
      });
      layer.on('mouseout', function () {
        if (popupOpen) return;
        this.setStyle(baseStyle);
      });
      layer.on('click', function () {
        if (this.isTooltipOpen()) this.closeTooltip();
        this.setStyle(baseStyle);
      });
      layer.on('popupopen', function (e) {
        popupOpen = true;
        this.setStyle(baseStyle);
        // Attach copy-button handler inside the popup
        const container = e.popup.getElement();
        if (container) {
          const copyBtn = container.querySelector('.alert-copy-id-btn');
          if (copyBtn) {
            copyBtn.addEventListener('click', function (evt) {
              evt.stopPropagation();
              const id = this.getAttribute('data-alert-id');
              if (id && navigator.clipboard) {
                navigator.clipboard.writeText(id).then(() => {
                  this.classList.add('copied');
                  this.textContent = '\u2713';
                  setTimeout(() => {
                    this.classList.remove('copied');
                    this.innerHTML = '\u{1F4CB}';
                  }, 1500);
                });
              }
            });
          }
        }
      });
      layer.on('popupclose', function () {
        popupOpen = false;
        this.setStyle(baseStyle);
      });
    },
  });
  wrapperGroup.addLayer(sharedGeoLayer);

  // ---------- helpers ----------
  function formatDateTime(date) {
    if (!date) return '';
    const y = date.getUTCFullYear();
    const mo = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    const h = String(date.getUTCHours()).padStart(2, '0');
    const mi = String(date.getUTCMinutes()).padStart(2, '0');
    return `${y}-${mo}-${d}T${h}:${mi}:00`;
  }

  /** Round a Date to the nearest 10 minutes */
  function roundToNearest10Min(date) {
    const d = new Date(date.getTime());
    const mins = d.getUTCMinutes();
    const rounded = Math.round(mins / 10) * 10;
    d.setUTCMinutes(rounded, 0, 0);
    return d;
  }

  function buildTileUrl(z, x, y) {
    // Pass the map's actual display zoom so the server can scale
    // geometry simplification to what the user sees, not the tile zoom.
    const displayZoom = _map ? Math.round(_map.getZoom()) : z;
    let url = `${serverRoot}/VisualCrossingWebServices/rest/api/v1/map/tile/` +
              `alerts/${z}/${x}/${y}.geojson?key=${key}&unitGroup=${unitGroup}` +
              `&displayZoom=${displayZoom}`;

    let dt = currentTime || (getDateTimeFn ? getDateTimeFn() : null);
    if (dt) {
      if (dt instanceof Date) {
        dt = roundToNearest10Min(dt);
        url += `&time=${formatDateTime(dt)}`;
      } else {
        // dt is an ISO string — parse, round, re-format
        const parsed = new Date(dt);
        if (!isNaN(parsed.getTime())) {
          url += `&time=${formatDateTime(roundToNearest10Min(parsed))}`;
        } else {
          url += `&time=${dt}`;
        }
      }
    }
    return url;
  }

  function formatAlertTime(isoStr) {
    if (!isoStr) return '';
    try {
      const d = new Date(isoStr);
      return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
        hour12: true,
      });
    } catch { return isoStr; }
  }

  function buildTooltipHtml(props) {
    const lines = [];
    if (props.event || props.severity) {
      const evHtml = props.event ? `<strong style="font-size:13px;">${escapeHtml(props.event)}</strong>` : '';
      const sevHtml = props.severity ? `<span style="background:${severityBadgeColor(props.severity)};color:#fff;border-radius:3px;padding:1px 6px;font-size:11px;white-space:nowrap;">${escapeHtml(props.severity)}</span>` : '';
      lines.push(`<div style="display:flex;align-items:center;justify-content:space-between;gap:8px;">${evHtml}${sevHtml}</div>`);
    }
    if (props.headline) {
      lines.push(`<div class="alert-headline-clamp" style="font-size:12px;margin-top:4px;max-width:300px;">${escapeHtml(props.headline)}</div>`);
    }
    if (props.onset || props.ends) {
      const from = formatAlertTime(props.onset);
      const to = formatAlertTime(props.ends);
      const timeStr = from && to ? `${from} – ${to}` : from || to;
      lines.push(`<div style="font-size:11px;color:#666;margin-top:4px;"><i class="bi bi-clock"></i> ${timeStr}</div>`);
    }
    if (props.link) {
      lines.push(`<div style="margin-top:4px;"><a href="${escapeHtml(props.link)}" target="_blank" rel="noopener" style="font-size:11px;">More info ›</a></div>`);
    }
    return lines.join('');
  }

  function buildPopupHtml(props, alertId) {
    const lines = [];
    if (props.event || props.severity) {
      const evHtml = props.event ? `<strong style="font-size:14px;">${escapeHtml(props.event)}</strong>` : '';
      const sevHtml = props.severity ? `<span style="background:${severityBadgeColor(props.severity)};color:#fff;border-radius:3px;padding:1px 6px;font-size:11px;white-space:nowrap;">${escapeHtml(props.severity)}</span>` : '';
      lines.push(`<div style="display:flex;align-items:center;justify-content:space-between;gap:8px;">${evHtml}${sevHtml}</div>`);
    }
    if (props.headline) {
      lines.push(`<div style="font-size:12px;margin-top:4px;word-wrap:break-word;">${escapeHtml(props.headline)}</div>`);
    }
    if (alertId) {
      lines.push(
        `<div style="font-size:11px;color:#666;margin-top:4px;display:flex;align-items:center;gap:4px;">` +
        `<span style="font-weight:600;">ID:</span>` +
        `<code class="alert-id-text" style="font-size:11px;background:#f0f0f0;padding:1px 4px;border-radius:3px;user-select:all;word-break:break-all;">${escapeHtml(alertId)}</code>` +
        `<button class="alert-copy-id-btn" data-alert-id="${escapeHtml(alertId)}" title="Copy alert ID" style="border:none;background:none;cursor:pointer;padding:2px 4px;line-height:1;font-size:13px;color:#666;">` +
        `&#x1F4CB;</button>` +
        `</div>`
      );
    }
    if (props.onset || props.ends) {
      const from = formatAlertTime(props.onset);
      const to = formatAlertTime(props.ends);
      const timeStr = from && to ? `${from} – ${to}` : from || to;
      lines.push(`<div style="font-size:11px;color:#666;margin-top:4px;"><i class="bi bi-clock"></i> ${timeStr}</div>`);
    }
    if (props.link) {
      lines.push(`<div style="margin-top:6px;"><a href="${escapeHtml(props.link)}" target="_blank" rel="noopener" style="font-size:11px;">More info ›</a></div>`);
    }
    return lines.join('');
  }

  function severityBadgeColor(severity) {
    switch ((severity || '').toLowerCase()) {
      case 'extreme': return '#dc3545';
      case 'severe':  return '#fd7e14';
      case 'moderate': return '#ffc107';
      case 'minor':   return '#0dcaf0';
      default:        return '#6c757d';
    }
  }

  function escapeHtml(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // ---------- tracing ----------
  const TRACE = false;
  let _tileSeq = 0;
  function _log(...args) {
    if (TRACE) console.log('[AlertVector]', ...args);
  }
  function _layerStats() {
    let pathCount = 0;
    sharedGeoLayer.eachLayer(() => pathCount++);
    return `sublayers=${pathCount} dedupSet=${renderedAlertIds.size} fetched=${fetchedTiles.size}`;
  }

  // ---------- tile computation ----------
  /**
   * Determine the alert tile zoom for the current map state.
   * Clamped between ALERT_MIN_ZOOM and ALERT_MAX_ZOOM.
   */
  function getAlertTileZoom(map) {
    return Math.max(ALERT_MIN_ZOOM, Math.min(ALERT_MAX_ZOOM, Math.round(map.getZoom())));
  }

  /** Compute tile coords covering the current map bounds at the alert tile zoom */
  function getVisibleTileCoords(map) {
    const z = getAlertTileZoom(map);
    const bounds = map.getPixelBounds();
    const tileSize = 256;
    const scale = Math.pow(2, z) / Math.pow(2, map.getZoom());
    const min = bounds.min.multiplyBy(scale).divideBy(tileSize).floor();
    const max = bounds.max.multiplyBy(scale).divideBy(tileSize).floor();

    const tiles = [];
    const maxTile = Math.pow(2, z) - 1;
    for (let x = min.x; x <= max.x; x++) {
      for (let y = Math.max(0, min.y); y <= Math.min(maxTile, max.y); y++) {
        // Wrap x for world copies
        const wx = ((x % (maxTile + 1)) + (maxTile + 1)) % (maxTile + 1);
        tiles.push({ z, x: wx, y });
      }
    }
    return tiles;
  }

  // ---------- fetch queue ----------
  function drainQueue() {
    while (inFlightCount < MAX_CONCURRENT && fetchQueue.length > 0) {
      const { tileKey, url } = fetchQueue.shift();

      // Skip if already fetched or aborted
      if (fetchedTiles.has(tileKey)) continue;

      const seq = ++_tileSeq;
      const controller = new AbortController();
      abortControllers.set(tileKey, controller);
      inFlightCount++;

      _log(`fetch #${seq} ${tileKey} (queue=${fetchQueue.length} inflight=${inFlightCount})`);
      const fetchStart = performance.now();

      fetch(url, { signal: controller.signal })
        .then(r => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then(fc => {
          const fetchMs = (performance.now() - fetchStart).toFixed(0);
          const totalFeatures = fc?.features?.length || 0;
          _log(`  #${seq} ${tileKey} done in ${fetchMs}ms — ${totalFeatures} features`);

          fetchedTiles.add(tileKey);

          if (!fc || !fc.features || fc.features.length === 0) return;

          const newFeatures = fc.features.filter(f => {
            const fid = f.id || (f.properties && f.properties.id);
            if (!fid) return false;
            if (renderedAlertIds.has(fid)) {
              return false;
            }
            renderedAlertIds.add(fid);
            return true;
          });

          if (newFeatures.length > 0) {
            // Track event types for the filter UI
            for (const f of newFeatures) {
              const ev = f.properties?.event;
              if (ev) {
                const key = ev.toLowerCase();
                const existing = knownEventTypes.get(key);
                if (existing) {
                  existing.count++;
                } else {
                  knownEventTypes.set(key, { label: ev, color: f.properties.fillColor || '#999', count: 1 });
                }
              }
            }
            sharedGeoLayer.addData({
              type: 'FeatureCollection',
              features: newFeatures,
            });
            // Apply current filter to newly added features
            if (hiddenEventTypes.size > 0) applyFilter();
            _log(`  #${seq} added ${newFeatures.length} new — ${_layerStats()}`);
          }
        })
        .catch(err => {
          if (err.name !== 'AbortError') {
            console.warn('Alert vector tile error:', tileKey, err);
          } else {
            _log(`  #${seq} ${tileKey} aborted`);
          }
        })
        .finally(() => {
          inFlightCount--;
          abortControllers.delete(tileKey);
          drainQueue();
        });
    }
  }

  /** Compute visible tiles and enqueue any that haven't been fetched yet */
  function loadVisibleTiles() {
    if (!_map) return;

    // When the tile zoom changes (user zoomed past a level boundary), the
    // server returns differently-simplified geometry — clear everything and
    // re-fetch at the new zoom.
    const tileZoom = getAlertTileZoom(_map);
    if (lastDisplayZoom >= 0 && tileZoom !== lastDisplayZoom) {
      _log(`tile zoom changed ${lastDisplayZoom}→${tileZoom} — clearing cache`);
      // Abort in-flight fetches for the old zoom
      abortControllers.forEach(c => c.abort());
      abortControllers.clear();
      fetchQueue = [];
      inFlightCount = 0;
      // Clear rendered data
      sharedGeoLayer.clearLayers();
      renderedAlertIds.clear();
      fetchedTiles.clear();
    }
    lastDisplayZoom = tileZoom;

    const tiles = getVisibleTileCoords(_map);
    let enqueued = 0;
    for (const { z, x, y } of tiles) {
      const tileKey = `${z}/${x}/${y}`;
      if (fetchedTiles.has(tileKey)) continue;
      // Don't enqueue if already in the queue
      if (fetchQueue.some(q => q.tileKey === tileKey)) continue;
      const url = buildTileUrl(z, x, y);
      fetchQueue.push({ tileKey, url });
      enqueued++;
    }
    if (enqueued > 0) {
      _log(`enqueued ${enqueued} tiles (total queue=${fetchQueue.length})`);
    }
    drainQueue();
  }

  // ---------- public API bolted onto the LayerGroup ----------

  wrapperGroup.refreshAlerts = function () {
    _log(`refreshAlerts — clearing ${_layerStats()}`);
    // Abort all in-flight fetches
    abortControllers.forEach(c => c.abort());
    abortControllers.clear();
    fetchQueue = [];
    inFlightCount = 0;
    // Clear rendered data
    sharedGeoLayer.clearLayers();
    renderedAlertIds.clear();
    fetchedTiles.clear();
    knownEventTypes.clear();
    // Re-fetch
    loadVisibleTiles();
  };

  wrapperGroup.setAlertTime = function (dt) {
    if (dt instanceof Date) {
      currentTime = formatDateTime(dt);
    } else {
      currentTime = dt;
    }
    this.refreshAlerts();
  };

  wrapperGroup.setAlertOpacity = function (val) {
    currentOpacity = val;
    sharedGeoLayer.eachLayer(subLayer => {
      const p = subLayer.feature?.properties || {};
      subLayer.setStyle({
        fillOpacity: (p.fillOpacity != null ? p.fillOpacity : 0.35) * val,
        opacity: (p.strokeOpacity != null ? p.strokeOpacity : 1) * val,
      });
    });
  };

  // ---------- event-type filter ----------

  /** Show/hide sub-layers based on hiddenEventTypes */
  function applyFilter() {
    sharedGeoLayer.eachLayer(subLayer => {
      const ev = (subLayer.feature?.properties?.event || '').toLowerCase();
      if (hiddenEventTypes.has(ev)) {
        subLayer.setStyle({ fillOpacity: 0, opacity: 0, weight: 0 });
        // Close any open tooltip/popup on hidden features
        if (subLayer.isTooltipOpen && subLayer.isTooltipOpen()) subLayer.closeTooltip();
        if (subLayer.isPopupOpen  && subLayer.isPopupOpen())  subLayer.closePopup();
        subLayer.options.interactive = false;
        if (subLayer._path) subLayer._path.style.pointerEvents = 'none';
      } else {
        const p = subLayer.feature?.properties || {};
        subLayer.setStyle({
          fillColor:   p.fillColor   || '#999999',
          fillOpacity: (p.fillOpacity != null ? p.fillOpacity : 0.35) * currentOpacity,
          color:       p.strokeColor || p.fillColor || '#999999',
          weight:      p.strokeWeight || 2,
          opacity:     (p.strokeOpacity != null ? p.strokeOpacity : 1) * currentOpacity,
        });
        subLayer.options.interactive = true;
        if (subLayer._path) subLayer._path.style.pointerEvents = '';
      }
    });
  }

  /**
   * Set the hidden event types and re-apply the filter.
   * @param {Set<string>|string[]} hidden  lowercased event type strings to hide
   */
  wrapperGroup.setEventFilter = function (hidden) {
    hiddenEventTypes.clear();
    for (const h of hidden) hiddenEventTypes.add(h.toLowerCase());
    applyFilter();
  };

  /** Get an array of { event, color, count, visible } for all known event types */
  wrapperGroup.getEventTypes = function () {
    const result = [];
    for (const [key, info] of knownEventTypes) {
      result.push({ event: key, label: info.label, color: info.color, count: info.count, visible: !hiddenEventTypes.has(key) });
    }
    result.sort((a, b) => b.count - a.count);
    return result;
  };

  // When added to a map: listen for moveend + datetimeupdated, then do initial load
  const origOnAdd = wrapperGroup.onAdd;
  wrapperGroup.onAdd = function (map) {
    if (origOnAdd) origOnAdd.call(this, map);
    _map = map;

    this._onMoveEnd = () => loadVisibleTiles();
    map.on('moveend', this._onMoveEnd);

    this._onDateTimeUpdated = (e) => {
      if (e.dateTime) {
        wrapperGroup.setAlertTime(e.dateTime);
      }
    };
    map.on('datetimeupdated', this._onDateTimeUpdated);

    // Initial load
    loadVisibleTiles();
  };

  const origOnRemove = wrapperGroup.onRemove;
  wrapperGroup.onRemove = function (map) {
    if (this._onMoveEnd) {
      map.off('moveend', this._onMoveEnd);
      this._onMoveEnd = null;
    }
    if (this._onDateTimeUpdated) {
      map.off('datetimeupdated', this._onDateTimeUpdated);
      this._onDateTimeUpdated = null;
    }
    abortControllers.forEach(c => c.abort());
    abortControllers.clear();
    fetchQueue = [];
    inFlightCount = 0;
    sharedGeoLayer.clearLayers();
    renderedAlertIds.clear();
    fetchedTiles.clear();
    knownEventTypes.clear();
    _map = null;
    if (origOnRemove) origOnRemove.call(this, map);
  };

  return wrapperGroup;
}
